import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  Download,
  Search,
} from 'lucide-react';
import {
  InvoiceRecord,
  CurrencyView,
  filterClientInvoiceRecords,
  invoiceRowInvoicedPaidOutstanding,
  invoiceEntityKey,
  entityKeyLabel,
  formatInvoiceDateShort,
  defaultClientInvoiceCurrencyView,
  escapeCsvCell,
  numForCsv,
  downloadTextFile,
  mergeInvoiceStatusChipOptions,
  normalizeClientProjectAssociations,
  aggregateInvoiceTotalsForProject,
  entityKeysForProject,
  aggregateInvoiceFinancialTotalsForSummary,
  computeDepositMetricsFromRecords,
} from '../../../../utils/invoiceUtils';
import { ClientDetailData } from '../../../../hooks/useClientDetail';
import { ClientDetailBillingEntityCards } from './ClientDetailBillingEntityCards';
import {
  ClientDetailRevenueOverviewCard,
  ClientDetailDepositPositionCard,
} from './ClientDetailKpiCards';
import type { ClientDetailSharedPeriod } from './client-detail-period-types';

/* ─── Helpers ────────────────────────────────────────────────────────────── */

const fmt = (n: number, ccy: string): string => {
  if (ccy === 'USD' || !ccy) return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  return `${n.toLocaleString('en-US', { maximumFractionDigits: 0 })} ${ccy}`;
};

const fmtUsd = (n: number): string =>
  `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

const statusBadgeClass = (status: string): string => {
  const s = (status || '').toLowerCase();
  if (s === 'paid') return 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200';
  if (s === 'overdue' || s === 'sent') return 'bg-amber-50 text-amber-700 ring-1 ring-amber-200';
  if (s === 'cancelled' || s === 'void') return 'bg-slate-100 text-slate-500 ring-1 ring-slate-200';
  if (s === 'draft') return 'bg-blue-50 text-blue-600 ring-1 ring-blue-200';
  return 'bg-slate-100 text-slate-600 ring-1 ring-slate-200';
};

type SortField = 'invoice_date' | 'invoice_number' | 'subject' | 'amount' | 'balance' | 'status';

/* ─── Props ──────────────────────────────────────────────────────────────── */

interface InvoiceDetailsTabProps {
  data: ClientDetailData;
  sharedPeriod: ClientDetailSharedPeriod;
  invoiceFilterResetSeq?: number;
  onInvoiceTableFiltersActiveChange?: (active: boolean) => void;
}

const PAGE_SIZE = 15;

/* ─── Component ──────────────────────────────────────────────────────────── */

export const InvoiceDetailsTab: React.FC<InvoiceDetailsTabProps> = ({
  data,
  sharedPeriod,
  invoiceFilterResetSeq = 0,
  onInvoiceTableFiltersActiveChange,
}) => {
  const invoiceRecordsRaw: InvoiceRecord[] = data?.detailed_invoices?.invoice_records || [];
  const baseCurrency =
    data?.metadata?.original_currency ||
    data?.currency_analysis?.primary_currency ||
    'USD';

  /* ── Filter state ──────────────────────────────────────────────── */
  /** Always invoice local / base currency; View toggle removed. */
  const currencyView: CurrencyView = useMemo(
    () => defaultClientInvoiceCurrencyView(data),
    [data],
  );
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [entityFilter, setEntityFilter] = useState<string[]>([]);
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [statusToolbarOpen, setStatusToolbarOpen] = useState(false);
  const statusToolbarRef = useRef<HTMLDivElement>(null);
  const lastInvoiceFilterResetSeq = useRef(0);

  /* ── Table state ───────────────────────────────────────────────── */
  const [sortBy, setSortBy] = useState<SortField>('invoice_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [displayCount, setDisplayCount] = useState(PAGE_SIZE);
  const [expandedRows, setExpandedRows] = useState<Set<string | number>>(new Set());

  /* ── Derived data ──────────────────────────────────────────────── */
  const projects = useMemo(
    () => normalizeClientProjectAssociations(data?.contact_profile?.project_associations),
    [data],
  );

  const billingEntityRows = useMemo(
    () =>
      projects.map((p) => ({
        project: p,
        totals: aggregateInvoiceTotalsForProject(p, invoiceRecordsRaw, currencyView),
        entityKeys: entityKeysForProject(p, invoiceRecordsRaw),
      })),
    [projects, invoiceRecordsRaw, currencyView],
  );

  const toggleBillingEntityCard = useCallback((keys: string[]) => {
    if (keys.length === 0) return;
    setEntityFilter((prev) => {
      const allSelected = keys.every((k) => prev.includes(k));
      if (allSelected) {
        return prev.filter((k) => !keys.includes(k));
      }
      const next = new Set(prev);
      keys.forEach((k) => next.add(k));
      return Array.from(next).sort();
    });
  }, []);

  const statusChipOptions = useMemo(
    () =>
      mergeInvoiceStatusChipOptions(
        invoiceRecordsRaw.flatMap((r) => {
          const s = r.status;
          if (s == null) return [];
          const t = String(s).trim();
          return t ? [t] : [];
        }),
      ),
    [invoiceRecordsRaw],
  );

  const statusToolbarSummary = useMemo(() => {
    if (statusFilter.length === 0) return 'All statuses';
    if (statusFilter.length === 1) return statusFilter[0];
    return `${statusFilter.length} selected`;
  }, [statusFilter]);

  useEffect(() => {
    if (!statusToolbarOpen) return;
    const handler = (e: MouseEvent) => {
      if (statusToolbarRef.current && !statusToolbarRef.current.contains(e.target as Node)) {
        setStatusToolbarOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [statusToolbarOpen]);

  /* ── Filtered records ──────────────────────────────────────────── */
  const filteredRecords = useMemo(() =>
    filterClientInvoiceRecords(invoiceRecordsRaw, {
      statusFilter,
      entityFilter,
      invoiceSearch,
      calendarYears: sharedPeriod.calendarYears,
      calendarMonths: sharedPeriod.calendarMonths.map((m) =>
        String(parseInt(String(m), 10)).padStart(2, '0'),
      ),
      fyYears: sharedPeriod.fyYears,
      fyQuarters: sharedPeriod.fyQuarters,
    }),
    [
      invoiceRecordsRaw,
      statusFilter,
      entityFilter,
      invoiceSearch,
      sharedPeriod.calendarYears,
      sharedPeriod.calendarMonths,
      sharedPeriod.fyYears,
      sharedPeriod.fyQuarters,
    ]
  );

  const filteredRevenueTotals = useMemo(
    () => aggregateInvoiceFinancialTotalsForSummary(filteredRecords),
    [filteredRecords],
  );

  /** Deposit KPI is client-level position — ignore header period (cal/FY), not table filters. */
  const invoiceRecordsForDepositKpi = useMemo(
    () =>
      filterClientInvoiceRecords(invoiceRecordsRaw, {
        statusFilter,
        entityFilter,
        invoiceSearch,
        calendarYears: [],
        calendarMonths: [],
        fyYears: [],
        fyQuarters: [],
      }),
    [invoiceRecordsRaw, statusFilter, entityFilter, invoiceSearch],
  );

  const filteredDepositMetrics = useMemo(
    () => computeDepositMetricsFromRecords(invoiceRecordsForDepositKpi),
    [invoiceRecordsForDepositKpi],
  );

  const showBaseClient = baseCurrency !== 'USD';

  /* ── Sorted records ────────────────────────────────────────────── */
  const sortedRecords = useMemo(() => {
    return [...filteredRecords].sort((a, b) => {
      let av: string | number = 0;
      let bv: string | number = 0;
      switch (sortBy) {
        case 'invoice_date': av = a.invoice_date || ''; bv = b.invoice_date || ''; break;
        case 'invoice_number': av = a.invoice_number || ''; bv = b.invoice_number || ''; break;
        case 'subject': av = a.subject || ''; bv = b.subject || ''; break;
        case 'status': av = a.status || ''; bv = b.status || ''; break;
        case 'amount':
          if (showBaseClient) {
            av = parseFloat(String(a.amount)) || parseFloat(String(a.amount_usd)) || 0;
            bv = parseFloat(String(b.amount)) || parseFloat(String(b.amount_usd)) || 0;
          } else {
            av = parseFloat(String(currencyView === 'USD' ? a.amount_usd : a.amount)) || 0;
            bv = parseFloat(String(currencyView === 'USD' ? b.amount_usd : b.amount)) || 0;
          }
          break;
        case 'balance':
          if (showBaseClient) {
            av = invoiceRowInvoicedPaidOutstanding(a, 'BASE').out;
            bv = invoiceRowInvoicedPaidOutstanding(b, 'BASE').out;
          } else {
            av = parseFloat(String(currencyView === 'USD' ? a.balance_usd : a.balance)) || 0;
            bv = parseFloat(String(currencyView === 'USD' ? b.balance_usd : b.balance)) || 0;
          }
          break;
      }
      if (typeof av === 'string' && typeof bv === 'string') {
        const c = av.localeCompare(bv);
        return sortDir === 'asc' ? c : -c;
      }
      const c = (av as number) - (bv as number);
      return sortDir === 'asc' ? c : -c;
    });
  }, [filteredRecords, sortBy, sortDir, currencyView, showBaseClient]);

  const visibleRecords = sortedRecords.slice(0, displayCount);

  const expandableInvoiceIds = useMemo(
    () =>
      sortedRecords
        .map((inv, idx) => ({ inv, idx }))
        .filter(({ inv }) => (inv.line_items || []).length > 0)
        .map(({ inv, idx }) => inv.invoice_id ?? idx),
    [sortedRecords],
  );

  const allLineItemsExpanded = useMemo(() => {
    if (expandableInvoiceIds.length === 0) return false;
    return expandableInvoiceIds.every((id) => expandedRows.has(id));
  }, [expandableInvoiceIds, expandedRows]);

  const toggleExpandAllLineItems = useCallback(() => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (expandableInvoiceIds.every((id) => next.has(id))) {
        expandableInvoiceIds.forEach((id) => next.delete(id));
      } else {
        expandableInvoiceIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [expandableInvoiceIds]);

  /* ── Helpers ───────────────────────────────────────────────────── */
  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
  };

  const toggleRow = (id: string | number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const hasPeriodFromBar =
    sharedPeriod.calendarYears.length > 0 ||
    sharedPeriod.calendarMonths.length > 0 ||
    sharedPeriod.fyYears.length > 0 ||
    sharedPeriod.fyQuarters.length > 0;
  const hasFilters = statusFilter.length > 0 || entityFilter.length > 0 ||
    invoiceSearch.trim() || hasPeriodFromBar;

  const invoiceTableFiltersActive =
    statusFilter.length > 0 || entityFilter.length > 0 || invoiceSearch.trim().length > 0;

  useEffect(() => {
    onInvoiceTableFiltersActiveChange?.(invoiceTableFiltersActive);
    return () => onInvoiceTableFiltersActiveChange?.(false);
  }, [invoiceTableFiltersActive, onInvoiceTableFiltersActiveChange]);

  useEffect(() => {
    if (invoiceFilterResetSeq <= lastInvoiceFilterResetSeq.current) return;
    lastInvoiceFilterResetSeq.current = invoiceFilterResetSeq;
    setStatusFilter([]);
    setEntityFilter([]);
    setInvoiceSearch('');
    setDisplayCount(PAGE_SIZE);
  }, [invoiceFilterResetSeq]);

  /* ── CSV export ────────────────────────────────────────────────── */
  const exportCsv = () => {
    const ccy = currencyView === 'USD' ? 'USD' : baseCurrency;
    const header = [
      '#', 'Invoice No.', 'Subject', 'Entity', 'Status',
      'Invoice Date', 'Due Date', 'Paid Date',
      `Amount (${ccy})`, 'Amount (USD)', 'Paid', 'Outstanding',
    ].map(escapeCsvCell).join(',');

    const rows = filteredRecords.map((inv, i) => {
      const r = invoiceRowInvoicedPaidOutstanding(inv, currencyView);
      return [
        String(i + 1),
        inv.invoice_number ?? '',
        inv.subject ?? '',
        entityKeyLabel(invoiceEntityKey(inv)),
        inv.status ?? '',
        inv.invoice_date ?? '',
        inv.due_date ?? '',
        inv.paid_date ?? '',
        numForCsv(r.inv),
        numForCsv(parseFloat(String(inv.amount_usd)) || 0),
        numForCsv(r.paid),
        numForCsv(r.out),
      ].map(escapeCsvCell).join(',');
    });

    const csv = `\uFEFF${[header, ...rows].join('\r\n')}`;
    const clientName = data?.contact_profile?.basic_info?.contact_name || 'client';
    downloadTextFile(`${clientName}_invoices.csv`, csv, 'text/csv;charset=utf-8;');
  };

  /* ── Sort header cell ──────────────────────────────────────────── */
  const ThSort: React.FC<{ field: SortField; label: string; className?: string }> = ({
    field, label, className = '',
  }) => {
    const active = sortBy === field;
    return (
      <th
        className={`px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 cursor-pointer select-none hover:text-slate-700 whitespace-nowrap ${className}`}
        onClick={() => handleSort(field)}
      >
        {label}
        <span className={`ml-1 ${active ? 'text-blue-500' : 'text-slate-300'}`}>
          {active ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
        </span>
      </th>
    );
  };

  /* ── Render ────────────────────────────────────────────────────── */
  const ccy = currencyView === 'USD' ? 'USD' : baseCurrency;
  const showBase = showBaseClient;
  const tableColCount = showBase ? 12 : 11;
  /** Local-currency clients: USD column stays in DOM for layout/export logic but is not shown. */
  const usdColumnHiddenClass = showBase ? 'hidden' : '';

  return (
    <div className="space-y-3">

      {/* KPI Cards + Entity Section — equal-height row */}
      <div className="flex gap-3 items-stretch">
        {/* Revenue Overview */}
        <div className="flex-1">
          <ClientDetailRevenueOverviewCard
            invoiced={filteredRevenueTotals.totalInvoicedUsd}
            outstanding={filteredRevenueTotals.outstandingUsd}
            invoicedDetail={
              showBase
                ? fmt(filteredRevenueTotals.totalInvoicedBase, baseCurrency)
                : undefined
            }
            outstandingDetail={
              showBase
                ? fmt(filteredRevenueTotals.outstandingBase, baseCurrency)
                : undefined
            }
          />
        </div>

        {/* Deposit Position */}
        <div className="flex-1">
          <ClientDetailDepositPositionCard
            deposit={filteredDepositMetrics.depositUsd}
            adjustment={filteredDepositMetrics.depositAdjustmentUsd}
            balance={filteredDepositMetrics.netDepositUsd}
            depositDetail={
              showBase ? fmt(filteredDepositMetrics.depositBase, baseCurrency) : undefined
            }
            adjustmentDetail={
              showBase
                ? fmt(filteredDepositMetrics.depositAdjustmentBase, baseCurrency)
                : undefined
            }
            balanceDetail={
              showBase ? fmt(filteredDepositMetrics.netDepositBase, baseCurrency) : undefined
            }
          />
        </div>

        {/* Entity Section */}
        {billingEntityRows.length > 0 && (
          <div className="flex-1 overflow-visible rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-md flex flex-col gap-1.5">
            <p className="text-[9px] font-semibold uppercase tracking-widest text-slate-400">
              Entity Filters
            </p>
            <div className="flex-1 flex items-center">
              <ClientDetailBillingEntityCards
                rows={billingEntityRows}
                displayCcy={ccy}
                entityFilter={entityFilter}
                onToggleEntityKeys={toggleBillingEntityCard}
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Invoice Table ─────────────────────────────────────────────── */}
      <div className="overflow-visible rounded-xl border border-slate-200 bg-white shadow-md">
        {/* Table toolbar */}
        <div className="border-b border-slate-200/80 bg-gradient-to-b from-slate-50/90 to-white px-4 py-3">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2.5">
            <div className="flex shrink-0 items-center gap-2 border-slate-200 pr-3 sm:border-r">
              <span className="text-sm font-semibold tabular-nums text-slate-800">
                {sortedRecords.length}
              </span>
              <span className="text-xs font-medium text-slate-500">
                invoice{sortedRecords.length !== 1 ? 's' : ''}
              </span>
              {hasFilters && (
                <span className="rounded-md bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700 ring-1 ring-blue-100">
                  Filtered
                </span>
              )}
            </div>

            <label htmlFor="invoice-details-search" className="sr-only">
              Search invoices
            </label>
            <div className="relative shrink-0">
              <Search
                className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
                aria-hidden
              />
              <input
                id="invoice-details-search"
                type="search"
                placeholder="Invoice # or keyword…"
                value={invoiceSearch}
                onChange={(e) => setInvoiceSearch(e.target.value)}
                className="h-9 w-[13.5rem] max-w-[min(13.5rem,calc(100vw-8rem))] rounded-lg border border-slate-200 bg-white py-2 pl-8 pr-3 text-xs text-slate-800 shadow-sm placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500/15"
              />
            </div>

            <div
              className="hidden h-7 w-px shrink-0 self-center bg-slate-200 md:block"
              aria-hidden
            />

            <div ref={statusToolbarRef} className="relative min-w-0 shrink-0">
              <button
                type="button"
                aria-haspopup="listbox"
                aria-expanded={statusToolbarOpen}
                aria-label="Filter by invoice status"
                onClick={() => setStatusToolbarOpen((v) => !v)}
                className={`flex h-9 w-[min(14rem,70vw)] min-w-[10.5rem] items-center justify-between gap-2 rounded-lg border bg-white px-2.5 text-left text-xs font-medium shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/15 ${
                  statusFilter.length > 0
                    ? 'border-blue-300 text-slate-800'
                    : 'border-slate-200 text-slate-600 hover:border-slate-300'
                }`}
              >
                <span className="truncate">{statusToolbarSummary}</span>
                <ChevronDown
                  size={14}
                  className={`shrink-0 text-slate-400 transition-transform ${statusToolbarOpen ? 'rotate-180' : ''}`}
                  aria-hidden
                />
              </button>
              {statusToolbarOpen && (
                <div
                  className="absolute left-0 top-full z-40 mt-1 w-[min(18rem,calc(100vw-2rem))] rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
                  role="listbox"
                  aria-multiselectable="true"
                >
                  <div className="max-h-56 overflow-y-auto px-1">
                    {statusChipOptions.map((s) => {
                      const keyLower = s.toLowerCase();
                      const checked = statusFilter.some(
                        (f) => f.trim().toLowerCase() === keyLower,
                      );
                      return (
                        <label
                          key={keyLower}
                          className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-slate-50"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              setStatusFilter((prev) => {
                                const exists = prev.find(
                                  (f) => f.trim().toLowerCase() === keyLower,
                                );
                                if (exists) {
                                  return prev.filter(
                                    (f) => f.trim().toLowerCase() !== keyLower,
                                  );
                                }
                                return [...prev, s];
                              });
                            }}
                            className="h-3.5 w-3.5 shrink-0 rounded border-slate-300 accent-blue-600"
                          />
                          <span
                            className={`min-w-0 flex-1 truncate text-xs font-medium ${
                              checked ? 'text-slate-900' : 'text-slate-600'
                            }`}
                          >
                            {s}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  {statusFilter.length > 0 && (
                    <div className="border-t border-slate-100 px-1 pt-1">
                      <button
                        type="button"
                        onClick={() => setStatusFilter([])}
                        className="w-full rounded-md px-2 py-1.5 text-left text-[11px] font-semibold text-slate-500 hover:bg-slate-50 hover:text-slate-800"
                      >
                        Clear status filter
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="ml-auto flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={toggleExpandAllLineItems}
                disabled={expandableInvoiceIds.length === 0}
                aria-expanded={allLineItemsExpanded}
                aria-label={
                  allLineItemsExpanded
                    ? 'Collapse line items for all invoices'
                    : 'Expand line items for all invoices'
                }
                title={
                  allLineItemsExpanded
                    ? 'Collapse line items for all filtered invoices'
                    : 'Expand line items for all filtered invoices'
                }
                className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:pointer-events-none disabled:opacity-40"
              >
                <ChevronsDownUp className="h-3.5 w-3.5 text-slate-500" strokeWidth={2} aria-hidden />
                {allLineItemsExpanded ? 'Collapse all' : 'Expand all'}
              </button>
              <button
                type="button"
                onClick={exportCsv}
                className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 text-xs font-semibold text-slate-700 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              >
                <Download className="h-3.5 w-3.5 text-slate-500" strokeWidth={2} />
                Export CSV
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 border-b-2 border-slate-200">
              <tr>
                <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 w-8">#</th>
                <ThSort field="invoice_number" label="Invoice No." />
                <ThSort field="subject" label="Subject" className="min-w-[160px]" />
                <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap">Entity</th>
                <ThSort field="status" label="Status" />
                <ThSort field="invoice_date" label="Invoice Date" />
                <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap">Due Date</th>
                <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap">Paid Date</th>
                <ThSort
                  field="amount"
                  label={`Amount (${showBase ? baseCurrency : ccy})`}
                  className="text-right"
                />
                {showBase && (
                  <th
                    className={`px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap ${usdColumnHiddenClass}`}
                    aria-hidden={showBase}
                  >
                    USD
                  </th>
                )}
                <th className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500">Paid</th>
                <ThSort field="balance" label="Outstanding" className="text-right" />
              </tr>
            </thead>
            <tbody>
              {visibleRecords.length === 0 ? (
                <tr>
                  <td colSpan={tableColCount} className="text-center py-16 text-slate-600 text-sm">
                    No invoices match your filters.
                  </td>
                </tr>
              ) : (
                visibleRecords.map((inv, idx) => {
                  const id = inv.invoice_id ?? idx;
                  const expanded = expandedRows.has(id);
                  const row = invoiceRowInvoicedPaidOutstanding(inv, currencyView);
                  const rowNative = invoiceRowInvoicedPaidOutstanding(inv, 'BASE');
                  const rowUsd = invoiceRowInvoicedPaidOutstanding(inv, 'USD');
                  const invCcy =
                    String(inv.currency || baseCurrency || 'USD').trim().toUpperCase() || baseCurrency;
                  const displayRow = showBase ? rowNative : row;
                  const displayCcy = showBase ? invCcy : ccy;
                  const hasLines = (inv.line_items || []).length > 0;

                  return (
                    <React.Fragment key={id}>
                      <tr
                        className={`border-t border-slate-100 transition-colors ${
                          idx % 2 === 1 ? 'bg-slate-50/60' : 'bg-white'
                        } ${hasLines ? 'cursor-pointer hover:bg-blue-50/50' : ''}`}
                        onClick={() => hasLines && toggleRow(id)}
                      >
                        <td className="px-3 py-2.5 text-slate-600 tabular-nums text-xs">{idx + 1}</td>
                        <td className="px-3 py-2.5 font-medium text-slate-800 whitespace-nowrap text-xs">
                          <span className="flex items-center gap-1">
                            {hasLines && (
                              expanded
                                ? <ChevronDown size={12} className="text-blue-400 flex-none" />
                                : <ChevronRight size={12} className="text-slate-300 flex-none" />
                            )}
                            {inv.invoice_number || `INV-${inv.invoice_id}`}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-slate-600 max-w-[200px] truncate text-xs">
                          {inv.subject || '—'}
                        </td>
                        <td className="px-3 py-2.5 text-slate-500 text-xs whitespace-nowrap">
                          {entityKeyLabel(invoiceEntityKey(inv))}
                        </td>
                        <td className="px-3 py-2.5">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusBadgeClass(inv.status || '')}`}>
                            {inv.status || '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-slate-500 text-xs whitespace-nowrap tabular-nums">
                          {formatInvoiceDateShort(inv.invoice_date)}
                        </td>
                        <td className="px-3 py-2.5 text-slate-600 text-xs whitespace-nowrap tabular-nums">
                          {formatInvoiceDateShort(inv.due_date)}
                        </td>
                        <td className="px-3 py-2.5 text-slate-600 text-xs whitespace-nowrap tabular-nums">
                          {formatInvoiceDateShort(inv.paid_date)}
                        </td>
                        <td className="px-3 py-2.5 text-right text-slate-800 tabular-nums text-xs font-semibold">
                          {fmt(displayRow.inv, displayCcy)}
                        </td>
                        {showBase && (
                          <td
                            className={`px-3 py-2.5 text-right text-slate-600 tabular-nums text-xs ${usdColumnHiddenClass}`}
                            aria-hidden={showBase}
                          >
                            {fmtUsd(rowUsd.inv)}
                          </td>
                        )}
                        <td className="px-3 py-2.5 text-right text-emerald-700 tabular-nums text-xs font-medium">
                          {fmt(displayRow.paid, displayCcy)}
                        </td>
                        <td className={`px-3 py-2.5 text-right tabular-nums text-xs font-semibold ${
                          displayRow.out > 0 ? 'text-amber-600' : 'text-slate-600'
                        }`}>
                          {fmt(displayRow.out, displayCcy)}
                        </td>
                      </tr>
                      {/* Expanded line items */}
                      {expanded && (inv.line_items || []).length > 0 && (
                        <tr>
                          <td colSpan={tableColCount} className="bg-blue-50/30 border-t border-blue-100 px-6 py-3">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-slate-600 border-b border-slate-200">
                                  <th className="text-left py-1.5 font-semibold">Description</th>
                                  <th className="text-right py-1.5 font-semibold">Qty</th>
                                  <th className="text-right py-1.5 font-semibold">Unit Price</th>
                                  <th className="text-right py-1.5 font-semibold">Tax %</th>
                                  <th className="text-right py-1.5 font-semibold whitespace-nowrap">
                                    Line Total ({invCcy})
                                  </th>
                                  <th
                                    className={`text-right py-1.5 font-semibold ${usdColumnHiddenClass}`}
                                    aria-hidden={showBase}
                                  >
                                    USD
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {(inv.line_items || []).map((line, li) => {
                                  const rawLt = line.line_total;
                                  let lineTotalFmt = '—';
                                  if (rawLt != null && String(rawLt).trim() !== '') {
                                    const n = parseFloat(String(rawLt));
                                    if (Number.isFinite(n)) {
                                      /* Header is "Line Total (invCcy)" — omit code on each row. */
                                      lineTotalFmt =
                                        invCcy === 'USD' || !invCcy
                                          ? fmt(n, 'USD')
                                          : n.toLocaleString('en-US', {
                                              maximumFractionDigits: 0,
                                            });
                                    }
                                  }
                                  return (
                                  <tr key={li} className="border-t border-slate-100/80">
                                    <td className="py-1.5 text-slate-700 max-w-[240px] pr-4">
                                      {line.description || '—'}
                                    </td>
                                    <td className="py-1.5 text-right text-slate-500 tabular-nums">
                                      {line.quantity ?? '—'}
                                    </td>
                                    <td className="py-1.5 text-right text-slate-500 tabular-nums">
                                      {line.unit_price ?? '—'}
                                    </td>
                                    <td className="py-1.5 text-right text-slate-500 tabular-nums">
                                      {line.tax_percentage != null ? `${line.tax_percentage}%` : '—'}
                                    </td>
                                    <td className="py-1.5 text-right text-slate-800 tabular-nums font-semibold">
                                      {lineTotalFmt}
                                    </td>
                                    <td
                                      className={`py-1.5 text-right text-slate-600 tabular-nums ${usdColumnHiddenClass}`}
                                      aria-hidden={showBase}
                                    >
                                      {line.line_total_usd ?? '—'}
                                    </td>
                                  </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Load more */}
        {sortedRecords.length > displayCount && (
          <div className="px-4 py-3 border-t border-slate-100 text-center bg-slate-50/50">
            <button
              onClick={() => setDisplayCount((c) => c + PAGE_SIZE)}
              className="text-[12px] font-semibold text-blue-600 hover:text-blue-800 transition-colors"
            >
              Load {Math.min(PAGE_SIZE, sortedRecords.length - displayCount)} more
              <span className="text-slate-600 font-normal ml-1">
                ({sortedRecords.length - displayCount} remaining)
              </span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceDetailsTab;
